<template>
  <v-card flat>
    <v-card-title class="d-flex align-center px-4 py-3">
      <div class="d-flex align-center flex-grow-1">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="changeMonth(-1)"
              class="mr-1"
            >
              <v-icon size="24">mdi-chevron-left</v-icon>
            </v-btn>
          </template>
          <span>上個月</span>
        </v-tooltip>

        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              outlined
              label
              v-bind="attrs"
              v-on="on"
              class="px-3"
              color="primary"
            >
              <v-icon left size="16">mdi-calendar</v-icon>
              {{ dateRangeText }}
            </v-chip>
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            no-title
            scrollable
            @input="dateMenu = false"
            color="blue"
            locale="zh-TW"
          >
          </v-date-picker>
        </v-menu>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="changeMonth(1)"
              class="ml-1"
            >
              <v-icon size="24">mdi-chevron-right</v-icon>
            </v-btn>
          </template>
          <span>下個月</span>
        </v-tooltip>
      </div>

      <v-btn
        icon
        small
        @click="fetchAbsentDays"
        :loading="loading"
        color="grey darken-1"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <v-data-table
        :headers="headers"
        :items="absentDaysData"
        :loading="loading"
        :items-per-page="itemsPerPage"
        disable-pagination
        class="table elevation-0"
        hide-default-footer
        :no-data-text="loading ? '讀取中...' : '目前無紀錄'"
        :expanded.sync="expanded"
        item-key="user.id"
        show-expand
        @click:row="onRowClick"
      >
        <template v-slot:item.user.name="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ item.user ? item.user.name : "-" }}
            </span>
          </div>
        </template>

        <template v-slot:item.user.email="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ item.user ? item.user.email : "-" }}
            </span>
          </div>
        </template>

        <template v-slot:item.dates="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ item.dates ? item.dates.length : 0 }} 天
            </span>
          </div>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-4">
            <v-data-table
              :headers="detailHeaders"
              :items="item.items"
              :items-per-page="item.items.length"
              hide-default-footer
              class="elevation-0"
              dense
            >
              <template v-slot:item.date="{ item }">
                <span class="text-sm">{{ item.date }}</span>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  text-color="white"
                  class="px-2"
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.clock_in_actual="{ item }">
                <span class="text-sm">
                  {{
                    item.clock_record
                      ? formatTimeOnly(item.clock_record.clock_in_actual)
                      : "-"
                  }}
                </span>
              </template>

              <template v-slot:item.clock_out_actual="{ item }">
                <span class="text-sm">
                  {{
                    item.clock_record
                      ? formatTimeOnly(item.clock_record.clock_out_actual)
                      : "-"
                  }}
                </span>
              </template>

              <template v-slot:item.applications="{ item }">
                <span class="text-sm">
                  {{
                    item.applications && item.applications.length > 0
                      ? `${item.applications.length} 筆`
                      : "-"
                  }}
                </span>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
    </v-card-text>

    <div class="d-flex justify-center py-4">
      <v-pagination
        v-model="page"
        :length="pageCount"
        @input="fetchAbsentDays"
        prev-icon="fa fa-angle-left"
        next-icon="fa fa-angle-right"
        color="#cb0c9f"
        circle
      ></v-pagination>
    </div>
  </v-card>
</template>

<script>
import { formatTimeOnly as formatTimeOnlyUtil } from "@/utils/time";

export default {
  name: "AbsentDaysWidget",
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const today = new Date().toISOString().substr(0, 7);
    return {
      loading: false,
      absentDaysData: [],
      dateMenu: false,
      selectedMonth: today,
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,
      itemsPerPage: 15,
      pagination: {
        currentPage: 1,
        pageSize: 15,
        totalItems: 0,
        totalPages: 1,
      },
      expanded: [],
      headers: [
        { text: "員工姓名", value: "user.name", sortable: false },
        { text: "部門", value: "user.department", sortable: false },
        // job_title
        { text: "職位", value: "user.job_title", sortable: false },
        // member_id
        { text: "員工編號", value: "user.member_id", sortable: false },
        { text: "異常天數", value: "dates", sortable: false },
        { text: "", value: "data-table-expand", sortable: false },
      ],
      detailHeaders: [
        { text: "日期", value: "date", sortable: false },
        { text: "上班打卡", value: "clock_in_actual", sortable: false },
        { text: "下班打卡", value: "clock_out_actual", sortable: false },
        { text: "申請紀錄", value: "applications", sortable: false },
        { text: "狀態", value: "status", sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchAbsentDays();
  },
  computed: {
    dateRangeText() {
      return this.selectedMonth;
    },
  },
  watch: {
    selectedCompany: {
      handler() {
        this.page = 1;
        this.fetchAbsentDays();
      },
      immediate: true,
    },
    selectedMonth() {
      this.page = 1;
      this.fetchAbsentDays();
    },
  },
  methods: {
    async fetchAbsentDays() {
      if (!this.selectedCompany) return;
      this.loading = true;
      try {
        const [year, month] = this.selectedMonth.split("-");

        const queryParams = new URLSearchParams({
          company_id: this.selectedCompany.id,
          year: year,
          month: parseInt(month),
          page: this.page,
          limit: this.itemsPerPage,
        });

        const response = await this.$axios.get(
          `/api/clock-records/company/absent-days?${queryParams}`
        );

        this.absentDaysData = response.data.data || [];
        this.pagination = response.data.pagination;
        if (this.pagination) {
          this.pageCount = this.pagination.totalPages || 0;
        }
      } catch (error) {
        console.error("Error fetching absent days:", error);
      } finally {
        this.loading = false;
      }
    },

    formatTimeOnly(dateTime) {
      return formatTimeOnlyUtil(dateTime);
    },
    getStatusColor(status) {
      if (!status) return "grey";
      if (status.includes("無打卡無請假")) return "red";
      if (status.includes("未滿工時")) return "orange";
      if (status.includes("遲到")) return "amber darken-2";
      if (status.includes("早退")) return "amber darken-2";
      return "grey";
    },
    onRowClick(item, { expand, isExpanded }) {
      expand(!isExpanded);
    },
    changeMonth(offset) {
      const [year, month] = this.selectedMonth.split("-").map(Number);
      const date = new Date(year, month - 1 + offset, 1);
      const newYear = date.getFullYear();
      const newMonth = String(date.getMonth() + 1).padStart(2, "0");
      this.selectedMonth = `${newYear}-${newMonth}`;
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:last-child td {
  border-bottom: 0;
}

.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:hover {
  background: #f8f9fa;
}
</style>
